import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
    Crown,
    Star,
    Handshake,
    Megaphone,
    Hotel,
    HeartHandshake,
    Sparkles,
    Trophy,
    Swords,
    Rocket,
    Target,
    Mic,
    Users,
    Headphones,
    DoorOpen,
    Box,
    Car,
    Eye,
    Newspaper,
    BadgeCheck,
    FileText,
} from "lucide-react";
import { BRANDING_OPPS, SPONSORSHIPS, PDF_BG, BRANDING_OPPS1 } from "../data/content";
import SectionBg from "./SectionBg";

const SPONSOR_ICONS = [Crown, Star, Handshake, Sparkles, Megaphone, Hotel, HeartHandshake];

const ACTIVATION_ZONES = [
    {
        title: "Competition Zone",
        icon: Trophy,
        items: [
            {
                icon: Swords,
                title: "Live Creative Battles",
                copy: "Real-time challenges. Real talent.",
            },
            {
                icon: Rocket,
                title: "Startup Pitch Arenas",
                copy: "Ideas that innovate. Pitches that inspire.",
            },
            {
                icon: Target,
                title: "Brand Challenges",
                copy: "Solve. Create. Impress global brands.",
            },
            {
                icon: Mic,
                title: "Performance Stage",
                copy: "Showcase your skills. Own the spotlight.",
            },
            {
                icon: Users,
                title: "Interactive Audience Engagement",
                copy: "Vote. Cheer. Be part of the action.",
            },
        ],
    },
    {
        title: "Experience Zone",
        icon: Headphones,
        items: [
            {
                icon: Mic,
                title: "Podcast Studio Setups",
                copy: "Record. Connect. Amplify your voice.",
            },
            {
                icon: Users,
                title: "Creator Lounge",
                copy: "Relax. Network. Collaborate.",
            },
            {
                icon: DoorOpen,
                title: "Digital Activation Booths",
                copy: "Engage. Explore. Experience brands.",
            },
            {
                icon: Box,
                title: "Immersive Installations",
                copy: "Step inside creativity.",
            },
            {
                icon: Car,
                title: "Futuristic Display Areas",
                copy: "The future of media & technology.",
            },
            {
                icon: Handshake,
                title: "Networking Experiences",
                copy: "Meaningful conversations. Real connections.",
            },
        ],
    },
];

const PARTNER_BENEFITS = [
    {
        icon: Eye,
        title: "Brand Visibility",
        points: [
            "High-impact brand placement across all festival touchpoints",
            "On-ground branding across venues & zones",
            "Logo placement on all official collaterals",
        ],
    },
    {
        icon: Users,
        title: "Audience Reach",
        points: [
            "Access to 5000+ industry professionals",
            "Engagement with creators, influencers & entrepreneurs",
            "Direct interaction with decision makers",
        ],
    },
    {
        icon: Newspaper,
        title: "Media Exposure",
        points: [
            "Coverage in national & digital media",
            "Logo visibility across PR campaigns",
            "Recognition in press releases & features",
        ],
    },
    {
        icon: Star,
        title: "Creator Collaborations",
        points: [
            "Opportunities to collaborate with top creators",
            "Brand integrations in creator campaigns",
            "Access to exclusive content partnerships",
        ],
    },
    {
        icon: Handshake,
        title: "Networking Access",
        points: [
            "Access to VIP networking lounges",
            "One-on-one meetings with industry leaders",
            "Exclusive roundtables & private sessions",
        ],
    },
    {
        icon: Box,
        title: "Experiential Branding",
        points: [
            "Custom activations & immersive brand experiences",
            "Product showcases & live demonstrations",
            "Interactive engagement opportunities",
        ],
    },
    {
        icon: Megaphone,
        title: "PR & Communication",
        points: [
            "Co-branded marketing opportunities",
            "Social media amplification across channels",
            "Digital campaigns & influencer promotions",
        ],
    },
    {
        icon: Trophy,
        title: "Industry Recognition",
        points: [
            "Positioning as a supporter of creativity & innovation",
            "Recognition during award ceremonies",
            "Association with a prestigious industry platform",
        ],
    },
    {
        icon: Hotel,
        title: "VIP Hospitality",
        points: [
            "VIP passes & premium seating at key events",
            "Hospitality lounges & exclusive experiences",
            "Meet & greet opportunities with keynote speakers",
        ],
    },
    {
        icon: FileText,
        title: "Content Integration",
        points: [
            "Content partnerships & branded storytelling",
            "Speaking opportunities & panel participation",
            "Thought leadership positioning across sessions",
        ],
    },
    {
        icon: BadgeCheck,
        title: "Long-Term Value",
        points: [
            "Building long-term brand equity in the creative economy",
            "Part of India's fastest-growing advertising & media ecosystem",
            "Sustained association with future editions & community engagement",
        ],
        wide: true,
    },
];

const BENEFIT_TAGS = [
    "Visibility",
    "Influence",
    "Culture",
    "Networking",
    "Innovation",
    "Entertainment",
    "Media",
    "Creators",
];

export function Branding() {
    return (
        <section
            id="branding"
            className="relative py-24 md:py-40 overflow-hidden"
            data-testid="branding-section"
        >
            <SectionBg src={PDF_BG.branding} opacity={0.6} />
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 mb-12 relative">
                <div className="grid grid-cols-12 gap-6 md:gap-12">
                    <div className="col-span-12 md:col-span-4 text-xs uppercase tracking-[0.3em] text-[#FF6B35] font-mono">
                        Branding Opportunities
                    </div>
                    <h2 className="col-span-12 md:col-span-8 min-w-0 max-w-full font-display font-bold text-[1.62rem] leading-[1.14] tracking-normal break-words [overflow-wrap:anywhere] sm:text-5xl sm:leading-[1.05] sm:tracking-tight lg:text-6xl">
                        Put your brand <span className="gradient-text">everywhere</span>.
                    </h2>
                </div>
            </div>

            {/* Scrolling outlined marquee */}
            <div className="relative py-6 mb-8">
                <Marquee gradient={false} speed={60}>
                    {BRANDING_OPPS.map((b, i) => (
                        <span
                            key={i}
                            className="inline-flex items-center gap-6 text-[5vw] md:text-[3.5vw] font-display font-black uppercase tracking-tight px-8 text-stroke text-white/20 leading-none"
                        >
                            {b}
                            <span className="text-[#FF6B35]">●</span>
                        </span>
                    ))}
                </Marquee>
            </div>
            <div className="relative py-2">
                <Marquee gradient={false} speed={50} direction="right">
                    {BRANDING_OPPS1.slice().reverse().map((b, i) => (
                        <span
                            key={i}
                            className="inline-flex items-center gap-6 text-[5vw] md:text-[3.5vw] font-display font-black uppercase tracking-tight px-8 leading-none text-white/90"
                        >
                            {b}
                            <span className="text-[#9D4CDD]">●</span>
                        </span>
                    ))}
                </Marquee>
            </div>

            <div className="max-w-[1440px] mx-auto px-6 md:px-12 mt-12">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                    {BRANDING_OPPS.map((b, i) => (
                        <motion.div
                            key={b}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.03 }}
                            className="px-5 py-4 rounded-2xl border border-white/20 bg-[#0F0F0F]/95 text-sm font-semibold text-white shadow-[0_12px_40px_rgba(0,0,0,0.35)] backdrop-blur hover:border-[#FF6B35]/70 hover:bg-[#FF6B35] hover:text-black transition cursor-default"
                            data-testid={`brand-opp-${i}`}
                        >
                            {b}
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

export function ActivationZones() {
    return (
        <section
            id="activation-zones"
            className="relative py-24 md:py-36 overflow-hidden"
            data-testid="activation-zones-section"
        >
            <SectionBg src={PDF_BG.branding} opacity={0.6} />
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(255,107,53,0.16),transparent_34%),linear-gradient(180deg,rgba(10,10,10,0.5),rgba(10,10,10,0.95))]" />
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
                <div className="grid grid-cols-12 gap-6 md:gap-12 mb-12 md:mb-16">
                    <div className="col-span-12 md:col-span-4 text-xs uppercase tracking-[0.3em] text-[#FF6B35] font-mono">
                        Festival Zones
                    </div>
                    <h2 className="col-span-12 md:col-span-8 min-w-0 max-w-full font-display font-bold text-[1.62rem] leading-[1.14] tracking-normal break-words [overflow-wrap:anywhere] sm:text-5xl sm:leading-[1.05] sm:tracking-tight lg:text-6xl">
                        Competition meets <span className="gradient-text">experience</span>.
                    </h2>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                    {ACTIVATION_ZONES.map((zone, zi) => {
                        const ZoneIcon = zone.icon;
                        return (
                            <motion.div
                                key={zone.title}
                                initial={{ opacity: 0, y: 36 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ delay: zi * 0.12, duration: 0.7 }}
                                className="relative overflow-hidden rounded-[28px] border border-[#D8A05D]/55 bg-[#090909]/80 p-5 sm:p-7 md:p-8 shadow-[0_24px_80px_rgba(0,0,0,0.45)] backdrop-blur"
                                data-testid={`activation-zone-${zi}`}
                            >
                                <div className="pointer-events-none absolute inset-x-8 top-20 h-px bg-gradient-to-r from-transparent via-[#FFB86B] to-transparent opacity-80" />
                                <div className="pointer-events-none absolute -right-24 -top-24 h-56 w-56 rounded-full bg-[#FF6B35]/10 blur-3xl" />

                                <div className="relative mb-6 flex items-center gap-4 border-b border-[#D8A05D]/25 pb-6 sm:gap-5">
                                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-[#D8A05D]/70 text-[#FFB86B] shadow-[0_0_34px_rgba(255,184,107,0.14)] sm:h-20 sm:w-20">
                                        <ZoneIcon size={28} strokeWidth={1.5} className="sm:h-[34px] sm:w-[34px]" />
                                    </div>
                                    <h3 className="min-w-0 font-display text-lg leading-tight sm:text-3xl font-bold uppercase tracking-wide text-[#F6C982] break-words [overflow-wrap:anywhere]">
                                        {zone.title}
                                    </h3>
                                </div>

                                <div className="relative divide-y divide-[#D8A05D]/18">
                                    {zone.items.map((item, ii) => {
                                        const ItemIcon = item.icon;
                                        return (
                                            <motion.div
                                                key={item.title}
                                                initial={{ opacity: 0, x: -18 }}
                                                whileInView={{ opacity: 1, x: 0 }}
                                                viewport={{ once: true }}
                                                transition={{ delay: 0.12 + ii * 0.04 }}
                                                className="group grid grid-cols-[42px_minmax(0,1fr)] gap-3 py-4 sm:grid-cols-[56px_minmax(0,1fr)] sm:gap-4 md:grid-cols-[64px_minmax(0,1fr)] md:gap-5"
                                                data-testid={`activation-zone-${zi}-item-${ii}`}
                                            >
                                                <div className="flex h-10 w-10 items-center justify-center text-[#FF9A5D] transition group-hover:text-[#F6C982] sm:h-12 sm:w-12 md:h-14 md:w-14">
                                                    <ItemIcon size={26} strokeWidth={1.5} className="sm:h-8 sm:w-8" />
                                                </div>
                                                <div>
                                                    <div className="min-w-0 text-base sm:text-lg font-semibold leading-tight text-white md:text-xl [overflow-wrap:anywhere]">
                                                        {item.title}
                                                    </div>
                                                    <p className="mt-1 text-sm leading-relaxed text-zinc-300 md:text-base [overflow-wrap:anywhere]">
                                                        {item.copy}
                                                    </p>
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

export function Sponsorships() {
    return (
        <section
            id="sponsorships"
            className="relative py-24 md:py-40 overflow-hidden"
            data-testid="sponsorships-section"
        >
            <SectionBg src={PDF_BG.sponsors} opacity={0.6} />
            <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] rounded-full bg-[#FF6B35]/10 blur-[180px]" />
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
                <div className="grid grid-cols-12 gap-6 md:gap-12 mb-12 md:mb-16">
                    <div className="col-span-12 md:col-span-4 text-xs uppercase tracking-[0.3em] text-[#FF6B35] font-mono">
                        Sponsorship Tiers
                    </div>
                    <h2 className="col-span-12 md:col-span-8 min-w-0 max-w-full font-display font-bold text-[1.62rem] leading-[1.14] tracking-normal break-words [overflow-wrap:anywhere] sm:text-5xl sm:leading-[1.05] sm:tracking-tight lg:text-6xl">
                        Partnership, not just <span className="text-stroke-orange">sponsorship</span>.
                    </h2>
                </div>

                <div className="space-y-3">
                    {SPONSORSHIPS.map((s, i) => {
                        const Icon = SPONSOR_ICONS[i] || Star;
                        const isTitle = i === 0;
                        return (
                            <motion.a
                                key={s.tier}
                                href="#contact"
                                initial={{ opacity: 0, x: -30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true, margin: "-30px" }}
                                transition={{ delay: i * 0.07, duration: 0.7 }}
                                className={`group grid grid-cols-12 gap-4 md:gap-6 items-center px-5 sm:px-6 md:px-10 py-6 md:py-8 rounded-3xl border transition-all duration-500 ${
                                    isTitle
                                        ? "border-[#FF6B35]/40 bg-gradient-to-r from-[#FF6B35]/10 via-[#9D4CDD]/5 to-transparent hover:border-[#FF6B35]"
                                        : "border-white/10 bg-[#141414] hover:border-[#FF6B35]/60"
                                }`}
                                data-testid={`sponsor-tier-${i}`}
                            >
                                <div className="col-span-12 sm:col-span-2 md:col-span-1 flex items-center justify-start">
                                    <div
                                        className={`w-12 h-12 rounded-xl border flex items-center justify-center transition-all ${
                                            isTitle
                                                ? "bg-[#FF6B35] border-[#FF6B35] text-black"
                                                : "border-white/10 text-[#FF6B35] group-hover:bg-[#FF6B35] group-hover:text-black"
                                        }`}
                                    >
                                        <Icon size={20} />
                                    </div>
                                </div>
                                <div className="col-span-12 sm:col-span-10 md:col-span-4 min-w-0 font-display text-[1.25rem] leading-tight sm:text-xl md:text-2xl font-bold tracking-normal md:tracking-tight group-hover:text-[#FF6B35] transition [overflow-wrap:anywhere]">
                                    {s.tier}
                                </div>
                                <div className="col-span-12 md:col-span-6 min-w-0 text-zinc-400 text-sm md:text-base [overflow-wrap:anywhere]">{s.note}</div>
                                <div className="hidden md:flex col-span-1 justify-end font-mono text-xs uppercase tracking-[0.25em] text-zinc-500 group-hover:text-[#FF6B35] transition">
                                    Enquire
                                </div>
                            </motion.a>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

export function PartnerBenefits() {
    return (
        <section
            id="partner-benefits"
            className="relative py-24 md:py-36 overflow-hidden"
            data-testid="partner-benefits-section"
        >
            <SectionBg src={PDF_BG.sponsors} opacity={0.6} />
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,rgba(255,184,107,0.16),transparent_34%),linear-gradient(180deg,rgba(10,10,10,0.72),rgba(10,10,10,0.9)_42%,rgba(10,10,10,0.96))]" />
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
                <div className="grid grid-cols-12 gap-6 md:gap-12 mb-12">
                    <div className="col-span-12 md:col-span-4 text-xs uppercase tracking-[0.3em] text-[#FF6B35] font-mono">
                        Jaipur Ad Fest 2026
                    </div>
                    <div className="col-span-12 md:col-span-8">
                        <h2 className="min-w-0 max-w-full font-display font-bold text-[1.62rem] leading-[1.14] tracking-normal break-words [overflow-wrap:anywhere] sm:text-5xl sm:leading-[1.05] sm:tracking-tight lg:text-6xl">
                            Sponsor & Partner <span className="gradient-text">Benefits</span>
                        </h2>
                        <p className="mt-4 max-w-3xl text-sm md:text-lg text-zinc-300 leading-relaxed">
                            Meaningful visibility, strategic networking & long-term cultural association.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {PARTNER_BENEFITS.map((benefit, i) => {
                        const Icon = benefit.icon;
                        return (
                            <motion.div
                                key={benefit.title}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-40px" }}
                                transition={{ delay: i * 0.035, duration: 0.6 }}
                                className={`group min-h-[250px] rounded-2xl border border-[#D8A05D]/30 bg-[#080808]/86 p-5 shadow-[0_16px_50px_rgba(0,0,0,0.38)] backdrop-blur-md transition hover:border-[#FFB86B]/75 hover:bg-[#15110D]/95 sm:p-6 ${
                                    benefit.wide ? "lg:col-span-2 xl:col-span-2" : ""
                                }`}
                                data-testid={`partner-benefit-${i}`}
                            >
                                <div className="mb-4 flex items-center gap-4">
                                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[#D8A05D]/50 bg-[#FFB86B]/10 text-[#F6C982] transition group-hover:bg-[#FF6B35] group-hover:text-black">
                                        <Icon size={22} strokeWidth={1.7} />
                                    </div>
                                    <h3 className="min-w-0 text-sm font-bold uppercase tracking-wide text-white md:text-[15px] [overflow-wrap:anywhere]">
                                        {benefit.title}
                                    </h3>
                                </div>
                                <ul className="space-y-2.5">
                                    {benefit.points.map((point) => (
                                        <li key={point} className="flex gap-2 text-xs leading-relaxed text-zinc-300 md:text-sm">
                                            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#D8A05D]" />
                                            <span className="min-w-0 [overflow-wrap:anywhere]">{point}</span>
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        );
                    })}
                </div>

                <div className="mt-6 rounded-2xl border border-[#D8A05D]/25 bg-[#14100C]/80 px-4 py-4 backdrop-blur">
                    <div className="grid grid-cols-1 gap-2 text-center sm:grid-cols-4 lg:grid-cols-8">
                        {BENEFIT_TAGS.map((tag) => (
                            <div
                                key={tag}
                                className="border-[#D8A05D]/20 py-2 text-[11px] uppercase tracking-[0.18em] text-[#F6C982] sm:border-r last:border-r-0"
                            >
                                {tag}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
